import type { Cell, Dir } from "../types";
import { DIR_DELTA, DIRS, dirBetween } from "./config";
import type { Rng } from "../rng";

function key(c: Cell): string {
  return `${c.r},${c.c}`;
}

function inb(c: Cell, size: number): boolean {
  return c.r >= 0 && c.c >= 0 && c.r < size && c.c < size;
}

function neighbors(c: Cell, size: number): { dir: Dir; cell: Cell }[] {
  const out: { dir: Dir; cell: Cell }[] = [];
  for (const d of DIRS) {
    const n = { r: c.r + DIR_DELTA[d].dr, c: c.c + DIR_DELTA[d].dc };
    if (inb(n, size)) out.push({ dir: d, cell: n });
  }
  return out;
}

function onEdge(c: Cell, size: number): boolean {
  return c.r === 0 || c.c === 0 || c.r === size - 1 || c.c === size - 1;
}

function oppositeish(start: Cell, end: Cell, size: number): boolean {
  const far = size - 1;
  if (start.r === 0 && end.r === far) return true;
  if (start.r === far && end.r === 0) return true;
  if (start.c === 0 && end.c === far) return true;
  if (start.c === far && end.c === 0) return true;
  if ((start.r === 0 || start.r === far) && (end.c === 0 || end.c === far) && start.c !== end.c) return true;
  if ((start.c === 0 || start.c === far) && (end.r === 0 || end.r === far) && start.r !== end.r) return true;
  const manh = Math.abs(start.r - end.r) + Math.abs(start.c - end.c);
  return manh >= size - 1;
}

function turns(path: Cell[]): number {
  let t = 0;
  for (let i = 2; i < path.length; i++) {
    const d0 = dirBetween(path[i - 2], path[i - 1]);
    const d1 = dirBetween(path[i - 1], path[i]);
    if (d0 && d1 && d0 !== d1) t++;
  }
  return t;
}

function consecutiveSame(path: Cell[]): number {
  let max = 1,
    run = 1;
  for (let i = 2; i < path.length; i++) {
    const d0 = dirBetween(path[i - 2], path[i - 1]);
    const d1 = dirBetween(path[i - 1], path[i]);
    if (d0 && d1 && d0 === d1) {
      run++;
      if (run > max) max = run;
    } else run = 1;
  }
  return max;
}

/**
 * Path of `length` correct steps (length+1 cells). Start on an edge,
 * finish on the opposite edge or a far corner. Prefers turns so four
 * stones feel like real choices.
 */
export function generatePath(size: number, length: number, rng: Rng): Cell[] {
  const steps = length;
  const edges: Cell[] = [];
  for (let i = 0; i < size; i++) {
    edges.push({ r: 0, c: i });
    edges.push({ r: size - 1, c: i });
    edges.push({ r: i, c: 0 });
    edges.push({ r: i, c: size - 1 });
  }
  const uniq = new Map<string, Cell>();
  for (const e of edges) uniq.set(key(e), e);
  const starts = [...uniq.values()];

  let best: Cell[] | null = null;
  let bestScore = -1;

  for (let tryN = 0; tryN < 120; tryN++) {
    const start = rng.pick(starts);
    const path = walk(start, size, steps, rng);
    if (!path || path.length !== steps + 1) continue;
    const end = path[path.length - 1];
    if (!oppositeish(start, end, size) && tryN < 80) continue;
    if (!onEdge(end, size) && tryN < 40) continue;
    const sc = turns(path) * 4 + (oppositeish(start, end, size) ? 8 : 0) - consecutiveSame(path);
    if (sc > bestScore) {
      bestScore = sc;
      best = path;
      if (turns(path) >= Math.min(3, steps - 2) && oppositeish(start, end, size) && consecutiveSame(path) <= 3) {
        return path;
      }
    }
  }
  if (best) return best;

  // Fallback: snake from left-center
  const sr = Math.floor(size / 2);
  const snake: Cell[] = [{ r: sr, c: 0 }];
  let dir: Dir = "E";
  while (snake.length < steps + 1) {
    const last = snake[snake.length - 1];
    const order: Dir[] = rng.shuffle([dir, ...DIRS.filter((d) => d !== dir)]);
    let placed = false;
    for (const d of order) {
      const n = { r: last.r + DIR_DELTA[d].dr, c: last.c + DIR_DELTA[d].dc };
      if (!inb(n, size)) continue;
      if (snake.some((c) => c.r === n.r && c.c === n.c)) continue;
      snake.push(n);
      dir = d;
      placed = true;
      break;
    }
    if (!placed) break;
  }
  if (snake.length === steps + 1) return snake;

  // Last resort: straight-ish along a row then turn
  const row = Math.min(sr, size - 1);
  const out: Cell[] = [];
  for (let c = 0; c < size && out.length < steps + 1; c++) out.push({ r: row, c });
  let r = row + 1;
  let c = size - 1;
  while (out.length < steps + 1) {
    if (r >= 0 && r < size && !out.some((x) => x.r === r && x.c === c)) out.push({ r, c });
    r++;
    if (r >= size) {
      r = 0;
      c = Math.max(0, c - 1);
    }
  }
  return out.slice(0, steps + 1);
}

function walk(start: Cell, size: number, steps: number, rng: Rng): Cell[] | null {
  const path: Cell[] = [start];
  const seen = new Set<string>([key(start)]);

  const dfs = (depth: number, lastDir: Dir | null): boolean => {
    if (depth === steps) return true;
    const cur = path[path.length - 1];
    let opts = neighbors(cur, size).filter((n) => !seen.has(key(n.cell)));
    if (!opts.length) return false;
    // Prefer turning
    if (lastDir) {
      const turn = opts.filter((o) => o.dir !== lastDir);
      const same = opts.filter((o) => o.dir === lastDir);
      opts = rng.shuffle(turn).concat(rng.shuffle(same));
    } else {
      opts = rng.shuffle(opts);
    }
    for (const o of opts) {
      path.push(o.cell);
      seen.add(key(o.cell));
      if (dfs(depth + 1, o.dir)) return true;
      path.pop();
      seen.delete(key(o.cell));
    }
    return false;
  };

  if (dfs(0, null)) return path;
  return null;
}

export { neighbors, inb };
